/*
 * Wmb, Worldline Merchant Billing.
 * Copyright (C) 2017-2019 Worldline
 * All Rights Reserved
 * This product is protected by copyright. Any copying or distribution
 * is forbidden without the agreement of Worldline.
 */
import {Injectable, Inject, inject} from '@angular/core';
import { Location } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import {Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn} from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { KeycloakService } from 'security/keycloak.service';
import { ConfigurationService } from 'app/core/services/configuration.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthenticatedRouteGuard  {

    isMerchantCreate: boolean = false;
    isMerchantEdit: boolean = false;
    isMerchantDelete: boolean = false;

    constructor(private keycloakService: KeycloakService, private router: Router, private locationService: Location,
        private configService: ConfigurationService,
        private translate: TranslateService,
        private notifService: NotifMessageService,
        @Inject(DOCUMENT) private document: any) {
        console.debug('[KeyCloak] AuthenticatedRouteGuard instanciated !!!')
    }

    // before use the route, verify that the user is authenticated !

    // show the "service" menu as defaut selected menu if user don't have  any REPORTING_* role
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        console.debug('[KeyCloak] AuthenticatedRouteGuard#canActivate called');
        if (this.keycloakService.isKeyCloakActive()) {
            // wait for end of initialization of Security Service ....
            return from(this.keycloakService.waitForInitializationEnd())
                // in case of error, the result is false !
                .pipe(catchError((err) => {
                    console.log('[KeyCloak] AuthenticatedRouteGuard#canActivate - KeyCloak Service not initialized !', err);
                    return of(false);
                })).pipe(switchMap((value): Observable<boolean> => {
                    if (value) {
                        if (this.keycloakService.isLoggedIn()) {
                            const sub = this.keycloakService.rolesObservable.subscribe(roles => {
                                if (roles.length !== 0) {
                                    if (route.data.requiredRoles.some((role : string) => role.startsWith("REPORTING"))) {
                                        if(roles.filter(role => role.startsWith('REPORTING')).length === 0){
                                            this.router.navigate(["/service"]);
                                        }
                                        sub.unsubscribe();
                                    }
                                }
                            });
                            return of(true);

                        } else {
                            this.keycloakService.notifyErrorNotAuthorized();

                            if (this.configService.config.security.authenticatedGuardFailedURI === undefined) {
                                console.log('[KeyCloak] AuthenticatedRouteGuard#canActivate - Not Authenticated ! but we can still pass !');
                                return of(true);
                            } else if (this.configService.config.security.authenticatedGuardFailedURI === 'LOGIN') {
                                console.log('[KeyCloak] AuthenticatedRouteGuard#canActivate - Not Authenticated ! Send A login Request !');

                                const uri: string = state.url;
                                const normalizedUri: string = this.locationService.prepareExternalUrl(uri);
                                const completeRedirectUrlAfterLogin: string = this.document.location.protocol +
                                    '//' + this.document.location.hostname + ':' + this.document.location.port +
                                    normalizedUri;

                                return this.keycloakService.login(completeRedirectUrlAfterLogin);
                            } else {
                                console.log('[KeyCloak] AuthenticatedRouteGuard#canActivate - Not Authenticated ! redirect to ' +
                                    this.configService.config.security.authenticatedGuardFailedURI);
                                this.router.navigate([this.configService.config.security.authenticatedGuardFailedURI]);
                                return of(false);
                            }
                        }
                    } else {
                        // keycloak not initialized !
                        console.log('[KeyCloak] AuthenticatedRouteGuard#canActivate - Keycloak not initialized ! continue anyway ...');
                        return of(true);
                    }
                })).pipe(take(1));
        } else {
            // keycloak non actif ... pas de secutite active ==> on laisse passer !
            return of(true);
        }
    }

    isEnabled(role: string): boolean {
        return this.keycloakService.getRoles().includes(role);
    }
}
const canActivateWithAuth: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    return inject(AuthenticatedRouteGuard).canActivate(route, state);
};
export { canActivateWithAuth };
